import React from "react";
import { Link } from "react-router-dom";
import { useOnboarding } from "../context/OnboardingContext";
import { useHelpOpener } from "../help/useHelp";
import { useI18n } from "../i18n";

/** Where each step is done, and the help topic that explains it. */
const STEP_LINKS = {
  products: { to: "/admin/products", help: "products" },
  buy_prices: { to: "/admin/products", help: "products" },
  device: { to: "/admin/devices", help: "devices" },
  first_sale: { to: "/", help: "register" },
  staff: { to: "/admin/staff", help: "staff" },
  menu_qr: { to: "/admin/shop", help: "shop" },
};

/**
 * "Get started": what a new shop does next, at the top of the dashboard.
 *
 * Every tick comes from the shop's own data (the server works it out), so a
 * step is never shown done while the data says otherwise — delete the product
 * and its step is open again. The owner can hide it; it also goes by itself
 * once everything is done.
 */
const GetStartedCard = () => {
  const { t } = useI18n();
  const { state, update } = useOnboarding();
  const openHelp = useHelpOpener();

  const checklist = state?.checklist;
  if (!checklist?.visible) return null;

  const hide = () => {
    if (window.confirm(t("onboarding.card.hideConfirm"))) update({ checklist_dismissed: true });
  };

  return (
    <section
      aria-label={t("onboarding.card.title")}
      className="mb-6 rounded-xl border border-primary-200 bg-white p-4 shadow-lg sm:p-6"
    >
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">🚀 {t("onboarding.card.title")}</h2>
          <p className="text-sm text-gray-500">
            {t("onboarding.card.progress", { done: checklist.done, total: checklist.total })}
          </p>
        </div>
        <button type="button" onClick={hide} className="shrink-0 text-sm text-gray-500 hover:underline">
          {t("onboarding.card.hide")}
        </button>
      </div>

      <div className="mb-4 h-2 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <div
          className="h-full rounded-full bg-primary-600 transition-all"
          style={{ width: `${(checklist.done / checklist.total) * 100}%` }}
        />
      </div>

      <ul className="divide-y">
        {checklist.steps.map((step) => {
          const link = STEP_LINKS[step.key];

          return (
            <li key={step.key} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-3">
              <span
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  step.done ? "bg-green-100 text-green-700" : "border-2 border-gray-300 text-transparent"
                }`}
                aria-label={step.done ? "done" : "to do"}
              >
                ✓
              </span>
              <div className="min-w-0 flex-1 basis-40">
                <p className={`font-medium ${step.done ? "text-gray-400 line-through" : "text-gray-800"}`}>
                  {t(`onboarding.card.steps.${step.key}.title`)}
                  {step.optional && !step.done && (
                    <span className="ms-2 text-xs font-normal text-gray-400">{t("onboarding.card.optional")}</span>
                  )}
                </p>
                {!step.done && (
                  <p className="text-sm text-gray-500">{t(`onboarding.card.steps.${step.key}.desc`)}</p>
                )}
              </div>
              {!step.done && (
                <div className="ms-auto flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => openHelp(link.help)}
                    aria-label={t("onboarding.card.help")}
                    className="h-9 w-9 rounded-full border border-gray-300 text-sm font-bold text-gray-600"
                  >
                    ?
                  </button>
                  <Link
                    to={link.to}
                    className="rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white"
                  >
                    {t(`onboarding.card.steps.${step.key}.cta`)}
                  </Link>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default GetStartedCard;
